<template>
  <div>
    <!-- Page header -->
    <CCard class="mb-4">
      <CCardBody class="p-4">
        <CRow class="align-items-center">
          <CCol md="7">
            <h1 class="h4 mb-1">Businesses</h1>
            <p class="text-muted mb-0">
              Every business you manage. The default business is the one you work in after signing in.
            </p>
          </CCol>
          <CCol md="5" class="d-flex justify-content-md-end mt-3 mt-md-0">
            <CButton
              color="light"
              class="mr-2"
              aria-label="Refresh"
              :disabled="loading"
              @click="refreshTable"
            >
              <CIcon name="cil-reload" />
            </CButton>
            <CButton color="primary" @click="addNew">
              <CIcon name="cil-plus" /> Add business
            </CButton>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <CCard>
      <CCardBody>
        <div v-if="loading && !items.length" class="text-center p-5">
          <CSpinner color="primary" />
        </div>
        <div v-else-if="error && !items.length" class="text-center p-5">
          <CIcon name="cil-warning" size="xl" class="text-danger mb-2" />
          <h2 class="h6">Couldn't load your businesses</h2>
          <p class="text-muted">Check your connection and try again.</p>
          <CButton color="primary" variant="outline" @click="refreshTable">
            <CIcon name="cil-reload" /> Retry
          </CButton>
        </div>
        <div v-else-if="!items.length" class="text-center p-5">
          <CIcon name="cil-building" size="xl" class="text-muted mb-2" />
          <h2 class="h6">No businesses yet</h2>
          <p class="text-muted">Add your business to start sending invoices and running payroll.</p>
          <CButton color="primary" @click="addNew">
            <CIcon name="cil-plus" /> Add business
          </CButton>
        </div>
        <template v-else>
          <div class="d-none d-lg-block">
            <CDataTable
              :items="items"
              :fields="fields"
              column-filter
              items-per-page-select
              :items-per-page="25"
              hover
              sorter
              pagination
              :loading="loading"
            >
              <template #isDefault="{ item }">
                <td>
                  <CBadge v-if="item.isDefault" color="primary">Default</CBadge>
                </td>
              </template>
              <template #show_details="{ item }">
                <td class="py-2 text-right text-nowrap">
                  <CButton
                    v-if="!item.isDefault"
                    color="primary"
                    variant="outline"
                    class="mr-1"
                    :disabled="settingDefaultId === item.id"
                    @click="setDefault(item)"
                  >
                    Make default
                  </CButton>
                  <CButton
                    color="primary"
                    variant="ghost"
                    :aria-label="`Edit ${item.name}`"
                    @click="onEdit(item)"
                  >
                    <CIcon name="cil-pencil" />
                  </CButton>
                  <CButton
                    color="danger"
                    variant="ghost"
                    :aria-label="`Delete ${item.name}`"
                    @click="showDeleteConfirmation(item)"
                  >
                    <CIcon name="cil-trash" />
                  </CButton>
                </td>
              </template>
            </CDataTable>
          </div>
          <div class="d-lg-none">
            <CCard v-for="item in items" :key="item.id" class="mb-2">
              <CCardBody class="p-3">
                <div class="d-flex justify-content-between align-items-start">
                  <div>
                    <div class="font-weight-bold">{{ item.name }}</div>
                    <CBadge v-if="item.isDefault" color="primary">Default</CBadge>
                  </div>
                  <div class="text-nowrap">
                    <CButton
                      color="primary"
                      variant="ghost"
                      :aria-label="`Edit ${item.name}`"
                      @click="onEdit(item)"
                    >
                      <CIcon name="cil-pencil" />
                    </CButton>
                    <CButton
                      color="danger"
                      variant="ghost"
                      :aria-label="`Delete ${item.name}`"
                      @click="showDeleteConfirmation(item)"
                    >
                      <CIcon name="cil-trash" />
                    </CButton>
                  </div>
                </div>
                <CButton
                  v-if="!item.isDefault"
                  color="primary"
                  variant="outline"
                  class="mt-2"
                  :disabled="settingDefaultId === item.id"
                  @click="setDefault(item)"
                >
                  Make default
                </CButton>
              </CCardBody>
            </CCard>
          </div>
        </template>
      </CCardBody>
    </CCard>

    <CModal :show.sync="warningModal" :centered="true" :close-on-backdrop="!deleting">
      <template #header>
        <h5 class="modal-title">Delete {{ itemToDelete.name }}?</h5>
      </template>
      <p class="mb-0">
        {{ itemToDelete.name }} and its settings will be removed. This can't be undone.
      </p>
      <template #footer>
        <CButton color="light" :disabled="deleting" @click="warningModal = false">
          Keep it
        </CButton>
        <CButton color="danger" :disabled="deleting" @click="confirmDelete">
          <CSpinner v-if="deleting" size="sm" class="mr-1" />
          Delete business
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import BusinessApi from "@/lib/businessApi";

const fields = [
  { key: "name", label: "Name" },
  { key: "isDefault", label: "Default" },
  {
    key: "show_details",
    label: "",
    _style: "width:260px",
    sorter: false,
    filter: false,
  },
];

export default {
  name: "BusinessList",
  data() {
    return {
      loading: true,
      error: false,
      deleting: false,
      settingDefaultId: null,
      items: [],
      fields,
      api: new BusinessApi(),
      warningModal: false,
      itemToDelete: {},
    };
  },
  mounted() {
    this.refreshTable();
  },

  methods: {
    setDefault(item) {
      var self = this;
      self.settingDefaultId = item.id;
      self.api
        .updateDefaultBusiness(item)
        .then(() => {
          self.$toast.success(`${item.name} is now your default business`);
          self.refreshTable();
        })
        .catch(({ data }) => {
          self.$toast.error(
            `Couldn't change your default business. ${helper.getErrorMessage(data)}`
          );
        })
        .finally(() => {
          self.settingDefaultId = null;
        });
    },

    refreshTable() {
      var self = this;
      self.loading = true;
      self.error = false;
      self.api
        .getListByCurrentTenant()
        .then((response) => {
          self.items = response.result || [];
        })
        .catch((err) => {
          console.error(err);
          self.error = true;
          if (self.items.length) {
            self.$toast.error("Couldn't refresh your businesses. Check your connection and try again.");
          }
        })
        .finally(() => {
          self.loading = false;
        });
    },
    onEdit(item) {
      this.$router.push({
        path: `/tenants/Business/${item.id}`,
      });
    },
    confirmDelete() {
      var self = this;
      self.deleting = true;
      this.api
        .delete(self.itemToDelete.id)
        .then(() => {
          self.$toast.success("Business deleted");
          self.warningModal = false;
          self.itemToDelete = {};
          self.refreshTable();
        })
        .catch(({ data }) => {
          self.$toast.error(
            `Couldn't delete the business. ${helper.getErrorMessage(data)}`
          );
        })
        .finally(() => {
          self.deleting = false;
        });
    },
    showDeleteConfirmation(item) {
      this.itemToDelete = item;
      this.warningModal = true;
    },
    addNew() {
      this.$router.push({ path: "/tenants/Business" });
    },
  },
};
</script>
